/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";
import "react-reflex/styles.css";

import getSchema from "../../lib/api/getSchema";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import AccessControl from "../functional/AccessControl";
import notificationState from "../../states/notificationState";
import DeleteRecordButton from "../input/DeleteRecordButton";
import MachineTypeEmbed from "./MachineTypeEmbed";
import layoutConf from "../../config/layoutConf";
import MachineTypeStateTransitionCreateButton from "../input/MachineTypeStateTransitionCreateButton";
import MachineTypeStateTransitionUpdateButton from "../input/MachineTypeStateTransitionUpdateButton";
import getList from "../../lib/api/getList";
/**
 * @COMPONENT_NAME MachineTypeStateTransition
 * @DESCRIPTION 설비 유형과 설비 유형별 상태 전이 데이터를 관리하는 화면 컴포넌트
 *             화면 위에 설비 유형 목록을 표시하고 아래에 해당 설비 유형에 대한 상태 전이 데이터 관리 화면을 제공
 *
 * @PASSTHROUGH_PROPS
 *   - {Object} schema - 하위 컴포넌트에 전달 될 테이블 스키마 정보
 *   - {Object} grid - 하위 컴포넌트에 전달 될 그리드 객체 참조
 *   - {Object} refresh - 하위 컴포넌트에 전달 될 API refresh 객체
 *   - {Function} onSuccess - 하위 컴포넌트에 전달 될 작업 성공 시 콜백
 *   - {Function} onFailure - 하위 컴포넌트에 전달 될 작업 실패 시 콜백
 *   - {Function} onNotifyWarning - 하위 컴포넌트에 전달 될 경고 알림 콜백
 *
 * @CALLBACKS
 *   - {Function} handleMachineTypeSelect - 설비 유형 선택 시 호출되는 콜백
 *   - {Function} notifySuccess - 성공 알림을 표시하는 콜백
 *   - {Function} notifyFailure - 실패 알림을 표시하는 콜백
 *   - {Function} notifyWarning - 경고 알림을 표시하는 콜백
 *   - {Function} onApiInitialized - API 초기화 시 호출되는 콜백
 *   - {Function} onInitialized - 그리드 초기화 시 호출되는 콜백
 */

const MachineTypeStateTransition = () => {
    const table = "MachineTypeStateTransition";
    const view = "VwMachineTypeStateTransition";

    const [height, setHeight] = useState(0);

    const [schema, setSchema] = useState();
    const [grid, setGrid] = useState();
    const [api, setApi] = useState();
    const [machineTypeContextDataNames, setMachineTypeContextDataNames] = useState([]);
    const [machineTypeStateTransitionSelect, setMachineTypeStateTransitionSelect] = useState();
    const [selectedMachineType, setSelectedMachineType] = useState();

    const { t } = useTranslation();
    const setNotification = useSetRecoilState(notificationState);

    const handleResize = () => {
        setHeight(document.documentElement.offsetHeight - 96);
    };

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        let mounted = true;

        async function schemaSetting() {
            let schema = await getSchema(table);
            if (mounted) {
                setSchema(schema);
            }
        }

        schemaSetting();
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (grid) {
            grid.selectionChanged.addHandler((grid, event) => {
                if (grid.selectedItems && grid.selectedItems[0]) {
                    setMachineTypeStateTransitionSelect(grid.selectedItems[0]);
                } else {
                    setMachineTypeStateTransitionSelect([]);
                }
            });

            grid.itemsSourceChanged.addHandler((grid, event) => {
                if (grid.selectedItems && grid.selectedItems[0]) {
                    setMachineTypeStateTransitionSelect(grid.selectedItems[0]);
                } else {
                    setMachineTypeStateTransitionSelect([]);
                }
            });
        }
    }, [grid]);

    useEffect(() => {
        let mounted = true;

        async function fetchContextDataNames() {
            // 유형을 고르기 전(처음 그릴 때)에는 /undefined 로 헛 요청하지 않는다
            if (!selectedMachineType?.machineTypeCode) {
                setMachineTypeContextDataNames([]);
                return;
            }

            try {
                const response = await getList(`VwMachineTypeContext/${selectedMachineType.machineTypeCode}`);
                const dataNames = response.data.map((item) => item.dataName);

                if (mounted) {
                    setMachineTypeContextDataNames(dataNames);
                }
            } catch (reason) {
                if (mounted) {
                    notifyFailure(reason.response ? reason.response.data : reason);
                }
            }
        }

        fetchContextDataNames();

        setApi((api) => {
            if (api) {
                api.refresh();
            }

            return api;
        });

        return () => {
            mounted = false;
        };
    }, [selectedMachineType]);

    const handleMachineTypeSelect = (machineType) => {
        setSelectedMachineType(machineType && machineType.length !== 0 ? machineType : null);
    };

    const onApiInitialized = (api) => {
        setApi(api);
    };

    const onInitialized = (grid) => {
        setGrid(grid);
    };

    const notifySuccess = (msg) => {
        setNotification({
			open: true,
			severity: "success",
			msg: msg,
		});
    };

    const notifyFailure = (msg) => {
        setNotification({
			open: true,
			severity: "error",
			msg: msg,
		});
    };

    const notifyWarning = (msg) => {
        setNotification({
			open: true,
			severity: "warning",
			msg: msg,
		});
    };

    const onSuccess = (msg) => {
        notifySuccess(msg);
        if (api) {
            api.refresh();
        }
    };

    const onFailure = (reason) => {
        notifyFailure(reason);
    };

    const onNotifyWarning = (msg) => {
        notifyWarning(msg);
        if (api) {
            api.refresh();
        }
    };

    return (
        <div style={{ height: height }}>
            <ReflexContainer orientation="horizontal">
                <ReflexElement flex={0.5}>
                    <MachineTypeEmbed onSelect={handleMachineTypeSelect} />
                </ReflexElement>

                <ReflexSplitter style={{ height: layoutConf.reflex.splitterHeight }} />

                <ReflexElement flex={0.5}>
                    <BasicCrudScreenType
                        headerTitle={t("term.machineTypeStateTransition")}
                        id={table}
                        table={table}
                        view={view}
                        onModifyViewUrl={(url) => `${url}/${selectedMachineType?.machineTypeCode}`}
                        onInitialized={onInitialized}
                        onApiInitialized={onApiInitialized}
                        noCreate
                        noEdit
                        noDelete
                        noExcel
                        onAppendButtonInFrontOfSearch={() => (
                            <>
                                <BasicCrudScreenActionItemLayout>
                                    <AccessControl accessTarget={schema && schema.name} accessMethod="POST">
                                        <MachineTypeStateTransitionCreateButton
                                            id={table}
                                            schema={schema}
                                            refresh={api}
                                            onSuccess={onSuccess}
                                            disabled={!selectedMachineType}
                                            selectedMachineType={selectedMachineType}
                                            machineTypeContextDataNames={machineTypeContextDataNames}
                                        />
                                    </AccessControl>
                                </BasicCrudScreenActionItemLayout>

                                <BasicCrudScreenActionItemLayout>
                                    <AccessControl
                                        accessTarget={schema && schema.name}
                                        accessMethod={schema && (schema.updateWithPatch ? "PATCH" : "PUT")}
                                    >
                                        <MachineTypeStateTransitionUpdateButton
                                            id={table}
                                            schema={schema}
                                            grid={grid}
                                            refresh={api}
                                            onSuccess={onSuccess}
                                            disabled={machineTypeStateTransitionSelect?.length === 0}
                                            selectedMachineType={selectedMachineType}
                                            machineTypeContextDataNames={machineTypeContextDataNames}
                                        />
                                    </AccessControl>
                                </BasicCrudScreenActionItemLayout>

                                <BasicCrudScreenActionItemLayout>
                                    <AccessControl accessTarget={schema && schema.name} accessMethod="DELETE">
                                        <DeleteRecordButton
                                            id={table}
                                            schema={schema}
                                            grid={grid}
                                            refresh={api}
                                            disabled={machineTypeStateTransitionSelect?.length === 0}
                                            color="secondary"
                                            onSuccess={onSuccess}
                                            onFailure={onFailure}
                                            onPartialFailure={onNotifyWarning}
                                        />
                                    </AccessControl>
                                </BasicCrudScreenActionItemLayout>
                            </>
                        )}
                    />
                </ReflexElement>
            </ReflexContainer>
        </div>
    );
};

export default MachineTypeStateTransition;
